import { SelectFormField } from "../../../common/formfields/SelectFormField";
import { DefinedPropertyObject, IEntityMinimalModel } from "../../../api/GenericTypes";
import {
  LabNotebookExperiment,
  LabNotebookExperimentFilters,
  LabNotebookExperimentStatus,
  LabNotebookExperimentStatusList,
} from "../../types/LabNotebookExperiment";
import { LabNotebookMinimalModel } from "../../types/LabNotebook";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { LabNotebooksVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/LabNotebooksVirtualizedSelectFormField";
import { EntityFilterSideBar } from "../../../common/sidebar/filtersidebar/EntityFilterBar";
import { ProjectsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import {
  ILockableEntityForm,
  LockableEntityDefaultFilterFormValues,
  lockableEntityFiltersToForm,
  lockableEntityFormToFilters,
} from "../../../common/sidebar/filtersidebar/GenericFilters/LockableEntityFilterFormModels";
import {
  ISignableEntityForm,
  SignableEntityDefaultFilterFormValues,
  signableEntityFiltersToForm,
  signableEntityFormToFilters,
} from "../../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterFormModels";
import {
  ISoftDeletableEntityForm,
  SoftDeletableEntityDefaultFilterFormValues,
  softDeletableEntityFiltersToForm,
  softDeletableEntityFormToFilters,
} from "../../../common/sidebar/filtersidebar/GenericFilters/SoftDeletableEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { SignableEntityFilterForm } from "../../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterForm";
import { defaultLabNotebookExperimentsFilters } from "./LabNotebookExperimentsTable";

interface LabNotebookExperimentStatusObject {
  id: LabNotebookExperimentStatus;
  name: LabNotebookExperimentStatus;
}

export interface LabNotebookExperimentsFilterForm
  extends ICombinedModificationRecordEntityForm,
    ILockableEntityForm,
    ISignableEntityForm,
    ISoftDeletableEntityForm {
  labNotebookIds?: LabNotebookMinimalModel[] | undefined | null;
  status?: LabNotebookExperimentStatusObject[] | undefined | null;
  labNotebookProjectIds?: IEntityMinimalModel[] | undefined | null;
}
const defaultValues: DefinedPropertyObject<LabNotebookExperimentsFilterForm> = {
  labNotebookIds: null,
  status: null,
  labNotebookProjectIds: null,
  ...CombinedModificationRecordEntityDefaultFormValues,
  ...LockableEntityDefaultFilterFormValues,
  ...SignableEntityDefaultFilterFormValues,
  ...SoftDeletableEntityDefaultFilterFormValues,
};
export const labNotebookExperimentsFormToFilters = (
  form: LabNotebookExperimentsFilterForm
): LabNotebookExperimentFilters => {
  let filters: LabNotebookExperimentFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
    ...lockableEntityFormToFilters(form),
    ...signableEntityFormToFilters(form),
    ...softDeletableEntityFormToFilters(form),
  };
  filters.labNotebookIds =
    Array.isArray(form.labNotebookIds) && form.labNotebookIds.length > 0 ? form.labNotebookIds?.map((d) => d.id) : null;
  filters.status = Array.isArray(form.status) && form.status.length > 0 ? form.status?.map((d) => d.id) : null;
  filters.labNotebookProjectIds =
    Array.isArray(form.labNotebookProjectIds) && form.labNotebookProjectIds.length > 0
      ? form.labNotebookProjectIds?.map((d) => d.id)
      : null;
  return filters;
};

/**
 * Converts filters to form values
 * @param filters LabNotebookExperimentFilters
 * @returns LabNotebookExperimentsFilterForm
 * @author @CorradoSurmanowicz
 */
export const labNotebookExperimentsFiltersToForm = (
  filters: LabNotebookExperimentFilters
): LabNotebookExperimentsFilterForm => {
  return {
    labNotebookIds: filters.labNotebookIds?.map((id) => ({ id, name: "", status: "ACTIVE" })) || null,
    status: filters.status?.map((id) => ({ id, name: id })) || null,
    labNotebookProjectIds: filters.labNotebookProjectIds?.map((id) => ({ id, name: "" })) || null,
    ...combinedModificationRecordEntityFiltersToForm(filters),
    ...lockableEntityFiltersToForm(filters),
    ...signableEntityFiltersToForm(filters),
    ...softDeletableEntityFiltersToForm(filters),
  };
};

export const LabNotebookExperimentFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
}: TabStoreFilterSidebarProps<
  LabNotebookExperiment,
  LabNotebookExperimentsFilterForm,
  LabNotebookExperimentFilters
>) => {
  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultLabNotebookExperimentsFilters}
      formToFilters={labNotebookExperimentsFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
    >
      {({ control, register, onReset }) => (
        <EntityFilterSideBar onReset={onReset}>
          <LabNotebooksVirtualizedSelectForm id="labNotebookIds" label="Notebook" control={control} isMulti />

          <ProjectsVirtualizedSelectForm id="labNotebookProjectIds" control={control} showControls={false} isMulti />

          <SelectFormField
            id="status"
            label="Experiment status"
            {...register}
            items={LabNotebookExperimentStatusList.map((e) => {
              return { id: e, name: e };
            })}
            control={control}
            horizontal={false}
            isMulti={true}
          />

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />

          <SignableEntityFilterForm control={control} initialValues={initialValues} />

          {/* <SoftDeletableEntityFilterForm control={control} currentTab={currentTab} initialValues={initialValues} /> */}
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
